// components/MapLocationPopup.tsx
import { useState, useCallback, useEffect, useMemo } from "react";
import Map, { Marker } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import styled from "styled-components";
import AppButton from "../../../../AppComponents/AppButton";
import { debounce } from "lodash";
import { useNotification } from "../../../../Hooks/useNotification/useNotification";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

const MAPBOX_TOKEN = process.env.REACT_APP_LOCATION

interface MapLocationPopupProps {
  onSave: (coords: { latitude: number; longitude: number }) => void;
  onCancel: () => void;
  initialData: any;
}

const MapLocationPopup = ({ onSave, onCancel,initialData }: MapLocationPopupProps) => {
  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const {showAlert} = useNotification();
  const [searchValue, setSearchValue] = useState("");
  const [viewState, setViewState] = useState<any>({
    latitude: 18.5204,
    longitude: 73.8567,
    zoom: 12
  });

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
  };
  const handleMapClick = useCallback((e: any) => {
    const { lat, lng } = e.lngLat;
    setSelectedCoords({ lat, lng });
  }, []);

  useEffect(() => {
  const lat = parseFloat(initialData?.value?.latitude);
  const lng = parseFloat(initialData?.value?.longitude);

  if (!isNaN(lat) && !isNaN(lng)) {
    setSelectedCoords({ lat, lng });
    setViewState((prev: any) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  }
}, [initialData?.value?.latitude, initialData?.value?.longitude]);
  const handleMapMove = useCallback(
    debounce((evt: any) => {
      setViewState(evt.viewState);
    }, 300),
    []
  );
  const handleSearch = async (value: any) => {
    if (!value) return;
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json?access_token=${MAPBOX_TOKEN}`
      );
      const data = await response.json();
      const result = data.features[0];

      if (result) {
        const [lng, lat] = result.center;
        setViewState((prev: any) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
          zoom: 14
        }));
        setSelectedCoords({ lat, lng });
        setSearchValue("");
      } else {
        showAlert("Location not found", "error");
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    }
  };

  const handleKeyDown = (event: any) => {
  if (event.key === "Enter") {
    handleSearch(searchValue);
  }
};

  return (
    <Backdrop>
      <Modal>
        <SearchContainer>
          <SearchOutlinedIcon />
          <SearchInput type="search"
            onChange={handleSearchChange}
            value = {searchValue}
            onKeyDown={handleKeyDown}
            placeholder="Search location"
          />
        </SearchContainer>
        <Map
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle="mapbox://styles/mapbox/streets-v11"
          initialViewState={{ latitude: 18.5204, longitude: 73.8567, zoom: 12 }}
          onClick={handleMapClick}
          viewState={viewState}
          onMove={handleMapMove}
          // onMove={(evt: any) => setViewState(evt.viewState)}
          attributionControl={false}
          style={{
            height: "400px",
            width: "100%"
          }}
        >
          {selectedCoords && (
            <Marker
              longitude={selectedCoords.lng}
              latitude={selectedCoords.lat}
              anchor="bottom"
            />
          )}
        </Map>

        <ButtonRow>
          <CancelButton variant="outline" onClick={onCancel}>Cancel</CancelButton>
          <SaveButton
            disabled={!selectedCoords}
            buttonStyle={{
              width: "100px"
            }}
            onClick={() =>
              selectedCoords &&
              onSave({ latitude: selectedCoords.lat, longitude: selectedCoords.lng })
            }
          >
            Save
          </SaveButton>
        </ButtonRow>
      </Modal>
    </Backdrop>
  );
};

export default MapLocationPopup;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  position: absolute;
  display: flex;
  top: 10px;
  left: 10px;
  background: #fff;
  border-radius: 30px;
  padding: 5px;
  align-items: center;
  z-index: 9999;
  width: 50%;
  height: 39px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2), 0 -1px 0px rgba(0, 0, 0, 0.02);
`;

const Modal = styled.div`
  position: relative;
  width: 90%;
  max-width: 800px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px;
  border-top: 1px solid #ddd;
  background: #f9f9f9;
`;

const CancelButton = styled(AppButton)`
  background: #ccc;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
`;

const SaveButton = styled(AppButton)`
  background: #007bff;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0px 10px;
  border: none;
  height: 100%;
  outline: none;
  border-radius: 30px;
  font-size: 16px;
`
